import React, { useEffect, useState } from 'react';
import { fetchOrderFilterOptions, fetchTradersData } from '../services/dataApi';
import { useAuth } from '../contexts/AuthContext';
import SearchBar from './SearchBar';
import DetailsPortal from './DetailsPortal';
import './OrderTools.css';

const REPORT_STATUSES = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const EMPTY_ORDER_FILTERS = {};

const FILTER_FIELD_GROUPS = [
  {
    title: 'Order',
    fields: [
      { key: 'method', label: 'Method', type: 'multi', roles: ['admin', 'trader', 'gateway'] },
      { key: 'fiatCurrency', label: 'Fiat Currency', type: 'multi', roles: ['admin', 'trader', 'gateway'] }
    ]
  },
  {
    title: 'Amounts',
    fields: [
      { key: 'amountFiat', label: 'Amount Fiat', type: 'number', roles: ['admin', 'trader', 'gateway'] },
      { key: 'amountUsdt', label: 'Amount USDT', type: 'number', roles: ['admin', 'trader'] },
      { key: 'price', label: 'Price', type: 'number', roles: ['admin', 'trader'] }
    ]
  },
  {
    title: 'Participants',
    fields: [
      { key: 'traderName', label: 'Trader Name', type: 'multi', roles: ['admin'] },
      { key: 'gateway', label: 'Gateway', type: 'multi', roles: ['admin', 'gateway'] }
    ]
  }
];

const hasOrderFilterValues = (filters) => Object.values(filters || {}).some(
  (value) => Array.isArray(value) ? value.length > 0 : value !== '' && value !== null && value !== undefined
);

const MultiSelectField = ({ label, value = [], options = [], loading, open, onToggle, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  const summary = selected.length === 0
    ? label
    : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  const toggleValue = (option) => {
    onChange(selected.includes(option)
      ? selected.filter((item) => item !== option)
      : [...selected, option]);
  };

  return (
    <div className="order-filter-field order-filter-multiselect">
      <span>{label}</span>
      <button
        type="button"
        className={`${open ? 'is-open' : ''}${selected.length ? ' has-value' : ''}`}
        onClick={onToggle}
      >
        <span>{summary}</span>
        <span aria-hidden="true">⌄</span>
      </button>
      {open && (
        <div className="order-filter-options">
          {loading ? (
            <div className="order-filter-options-empty">Loading options...</div>
          ) : options.length ? options.map((option) => (
            <label key={option}>
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggleValue(option)}
              />
              <span>{option}</span>
            </label>
          )) : (
            <div className="order-filter-options-empty">No options available</div>
          )}
          {selected.length > 0 && (
            <button type="button" className="order-filter-options-clear" onClick={() => onChange([])}>
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const getBrowserTimezone = () => String(-new Date().getTimezoneOffset());

export const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return [formatDateInput(monthStart), formatDateInput(today)];
};

const DATE_RANGE_PRESETS = [
  { key: 'all', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: '7 days' },
  { key: 'month', label: 'This month' }
];

const getDateRangePreset = (preset) => {
  const today = new Date();
  const to = formatDateInput(today);

  if (preset === 'all') return ['', ''];
  if (preset === 'today') return [to, to];
  if (preset === 'week') {
    const from = new Date(today);
    from.setDate(today.getDate() - 6);
    return [formatDateInput(from), to];
  }

  return getDefaultDateRange();
};

export const getTimezoneOptions = () => {
  const localOffset = getBrowserTimezone();
  return [
    { id: 'utc', value: '0', label: 'UTC' },
    { id: 'plus-3', value: '180', label: '+3' },
    {
      id: 'local',
      value: localOffset,
      label: `Local time (${getTimezoneOffsetLabel(localOffset)})`
    }
  ];
};

const getTimezoneOffsetMs = (timezone) => {
  return Number(timezone || 0) * 60 * 1000;
};

const dateInTimezoneToUtc = (dateValue, endOfDay, timezone, timeValue = '') => {
  if (!dateValue) return null;
  const [year, month, day] = dateValue.split('-').map(Number);
  const defaultTime = endOfDay ? '23:59:59' : '00:00:00';
  const [hours, minutes, seconds] = (timeValue || defaultTime).split(':').map(Number);
  const milliseconds = endOfDay ? 999 : 0;
  const wallClockUtc = Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds);
  const result = wallClockUtc - getTimezoneOffsetMs(timezone);
  return new Date(result);
};

export const getTimezoneOffsetLabel = (timezone) => {
  const offsetMinutes = Math.round(getTimezoneOffsetMs(timezone) / 60000);
  if (offsetMinutes === 0) return 'UTC';

  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, '0');
  const minutes = String(absoluteMinutes % 60).padStart(2, '0');
  return `${sign}${hours}:${minutes}`;
};

export const formatSelectedDate = (dateValue) => {
  if (!dateValue) return '';
  const [year, month, day] = dateValue.split('-');
  return `${day}.${month}.${year}`;
};

export const toRangeBoundary = (
  date, endOfDay = false, timezone = getBrowserTimezone(), timeValue = ''
) => {
  if (!date) return '';
  const value = dateInTimezoneToUtc(date, endOfDay, timezone, timeValue);
  return Number.isNaN(value.getTime()) ? '' : value.toISOString();
};

const getOrderData = (order) => order?.data || order || {};

const formatCreated = (order, timezone) => {
  const data = getOrderData(order);
  const rawValue = data.created || data.Created || data.create_time || order?.create_time;
  if (!rawValue) return '';

  const numericValue = Number(rawValue);
  const date = Number.isFinite(numericValue)
    ? new Date(numericValue < 1000000000000 ? numericValue * 1000 : numericValue)
    : new Date(rawValue);
  if (Number.isNaN(date.getTime())) return String(rawValue);

  const zonedDate = new Date(date.getTime() + Number(timezone || 0) * 60 * 1000);
  const day = String(zonedDate.getUTCDate()).padStart(2, '0');
  const month = String(zonedDate.getUTCMonth() + 1).padStart(2, '0');
  const year = zonedDate.getUTCFullYear();
  const hours = String(zonedDate.getUTCHours()).padStart(2, '0');
  const minutes = String(zonedDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(zonedDate.getUTCSeconds()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
};

const protectCsvValue = (value) => {
  const normalized = value === null || value === undefined ? '' : String(value);
  const safeValue = /^[=+\-@\t\r]/.test(normalized) ? `'${normalized}` : normalized;
  return `"${safeValue.replace(/"/g, '""')}"`;
};

const makeTraderLookup = (traders) => new Map(traders.map((trader) => {
  const id = trader.traderId || trader.TraderId || trader.TRADER_ID || trader.ref;
  const name = trader.name || trader.Name || trader.NAME || trader.login || trader.Login || id;
  return [String(id || ''), name || ''];
}));

const buildReportRows = (orders, role, traderLookup, timezone, pageType) => {
  const columns = [
    { label: 'Created', roles: ['admin', 'trader', 'gateway'], value: (order) => formatCreated(order, timezone) },
    {
      label: 'Order ID',
      roles: ['admin', 'trader', 'gateway'],
      value: (_, data) => data.OrderId || data.orderId || data.order_id || ''
    },
    ...(pageType === 'payin' ? [
      {
        // Matches the D badge in the orders table: it marks an order copied
        // from another one.
        label: 'Duplicate',
        roles: ['admin', 'trader', 'gateway'],
        value: (_, data) => {
          const duplicatedFrom = data.duplicatedFrom;
          return duplicatedFrom !== null && duplicatedFrom !== undefined && duplicatedFrom !== '' ? 'D' : '';
        }
      },
      {
        label: 'Duplicated From',
        roles: ['admin', 'trader', 'gateway'],
        value: (_, data) => data.duplicatedFrom ?? ''
      },
      {
        label: 'Duplicated To',
        roles: ['admin', 'trader', 'gateway'],
        value: (_, data) => data.double === true ? (data.duplicateTo ?? '') : ''
      }
    ] : []),
    {
      label: 'Request ID',
      roles: ['admin', 'gateway'],
      value: (_, data) => data.requestId || data.RequestId || data.REQUEST_ID || ''
    },
    ...(pageType === 'payin' ? [{
      label: 'client_id',
      roles: ['admin'],
      value: (_, data) => data.clientData?.client_id ?? ''
    }] : []),
    ...[{
      label: 'client_name',
      roles: ['admin', 'trader', 'gateway'],
      value: (_, data) => data.clientData?.client_name ?? ''
    }, {
      label: 'client_surname',
      roles: ['admin', 'trader', 'gateway'],
      value: (_, data) => data.clientData?.client_surname ?? ''
    }],
    {
      label: 'Status',
      roles: ['admin', 'trader', 'gateway'],
      value: (_, data) => data.Status || data.status || ''
    },
    {
      label: 'Type',
      roles: ['admin', 'trader', 'gateway'],
      value: (_, data) => data.type || data.Type || data.TYPE || ''
    },
    {
      label: 'Method',
      roles: ['admin', 'trader', 'gateway'],
      value: (_, data) => data.Method || data.method || data.METHOD || ''
    },
    {
      label: 'Amount Fiat',
      roles: ['admin', 'trader', 'gateway'],
      value: (_, data) => data.AmountFiat ?? data.amountFiat ?? data.Amount ?? data.amount ?? ''
    },
    {
      label: 'Fiat Currency',
      roles: ['admin', 'trader', 'gateway'],
      value: (_, data) => data.FiatCurrency || data.fiatCurrency || data.Currency || data.currency || ''
    },
    {
      label: 'Amount USDT',
      roles: ['admin', 'trader'],
      value: (_, data) => data.AmountUsdt ?? data.amountUsdt ?? data.USDT ?? data.usdt ?? ''
    },
    {
      label: 'Price',
      roles: ['admin', 'trader'],
      value: (_, data) => data.Price ?? data.price ?? ''
    },
    {
      label: 'Fee USDT',
      roles: ['admin', 'trader'],
      value: (_, data) => data.feeUsdt ?? data.FeeUsdt ?? data.fee_usdt ?? ''
    },
    {
      label: 'Trader Name',
      roles: ['admin'],
      value: (_, data) => {
        const traderId = data.traderId || data.TraderId || data.TRADER_ID || '';
        return data.traderName || traderLookup.get(String(traderId)) || traderId;
      }
    },
    {
      label: 'Trader ID',
      roles: ['admin', 'trader'],
      value: (_, data) => data.traderId || data.TraderId || data.TRADER_ID || ''
    },
    {
      label: 'Gateway',
      roles: ['admin', 'gateway'],
      value: (_, data) => data.Gateway || data.gateway || ''
    },
    {
      label: 'Gateway ID',
      roles: ['admin'],
      value: (_, data) => data.gatewayId || data.GatewayId || data.GATEWAY_ID || ''
    }
  ];
  const visibleColumns = columns.filter((column) => column.roles.includes(role));
  const rows = orders.map((order) => {
    const data = getOrderData(order);
    return visibleColumns.map((column) => column.value(order, data));
  });

  return [visibleColumns.map((column) => column.label), ...rows];
};

const downloadCsv = (rows, filename) => {
  const csv = rows.map((row) => row.map(protectCsvValue).join(',')).join('\r\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const fetchReportOrders = async (fetchOrders, status, dateFrom, dateTo) => {
  const limit = 10000;
  const firstResult = await fetchOrders(status, 1, limit, '', dateFrom, dateTo);
  const orders = Array.isArray(firstResult?.data) ? [...firstResult.data] : [];
  const totalPages = Math.max(1, Number(firstResult?.pagination?.totalPages) || 1);

  for (let page = 2; page <= totalPages; page += 1) {
    const result = await fetchOrders(status, page, limit, '', dateFrom, dateTo);
    if (Array.isArray(result?.data)) {
      orders.push(...result.data);
    }
  }

  return orders;
};

const PickerShell = ({ title, onClose, children, className = '' }) => (
  <div className={`order-picker-popover ${className}`} onClick={(event) => event.stopPropagation()}>
    <div className="order-picker-header">
      <strong>{title}</strong>
      <button type="button" onClick={onClose} aria-label={`Close ${title}`}>×</button>
    </div>
    <div className="order-picker-body">{children}</div>
  </div>
);

export const CalendarPicker = ({ value, min, max, onChange, onClose }) => {
  const selected = value ? new Date(`${value}T00:00:00`) : new Date();
  const [viewDate, setViewDate] = useState(
    () => new Date(selected.getFullYear(), selected.getMonth(), 1)
  );
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const previousMonthDays = new Date(year, month, 0).getDate();
  const monthLabel = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(viewDate);

  const calendarDays = Array.from({ length: 42 }, (_, index) => {
    const dayOffset = index - firstWeekday + 1;
    const cellDate = dayOffset < 1
      ? new Date(year, month - 1, previousMonthDays + dayOffset)
      : dayOffset > daysInMonth
        ? new Date(year, month + 1, dayOffset - daysInMonth)
        : new Date(year, month, dayOffset);
    const dateValue = formatDateInput(cellDate);
    return {
      day: cellDate.getDate(),
      value: dateValue,
      outside: cellDate.getMonth() !== month,
      disabled: Boolean((min && dateValue < min) || (max && dateValue > max))
    };
  });

  return (
    <PickerShell title="Choose date" onClose={onClose} className="calendar-picker">
      <div className="calendar-toolbar">
        <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))}>‹</button>
        <strong>{monthLabel}</strong>
        <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))}>›</button>
      </div>
      <div className="calendar-weekdays">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className="calendar-days">
        {calendarDays.map((day) => (
          <button
            type="button"
            key={day.value}
            className={`${day.outside ? 'is-outside' : ''}${day.value === value ? ' is-selected' : ''}`}
            disabled={day.disabled}
            onClick={() => { onChange(day.value); onClose(); }}
          >
            {day.day}
          </button>
        ))}
      </div>
    </PickerShell>
  );
};

export const TimePicker = ({ value, onChange, onClose }) => {
  const [hours, minutes, seconds] = (value || '00:00:00').split(':');
  const [draft, setDraft] = useState({ hours, minutes, seconds });
  const units = [
    { key: 'hours', label: 'Hours', max: 23 },
    { key: 'minutes', label: 'Minutes', max: 59 },
    { key: 'seconds', label: 'Seconds', max: 59 }
  ];

  const changeUnit = (key, nextValue, max) => {
    const numeric = Math.min(Math.max(Number(nextValue) || 0, 0), max);
    setDraft((current) => ({ ...current, [key]: String(numeric).padStart(2, '0') }));
  };

  const stepUnit = (key, direction, max) => {
    const current = Number(draft[key]);
    const next = (current + direction + max + 1) % (max + 1);
    changeUnit(key, next, max);
  };

  return (
    <PickerShell title="Choose time" onClose={onClose} className="time-picker">
      <div className="time-columns">
        {units.map((unit) => (
          <div className="time-column" key={unit.key}>
            <span>{unit.label}</span>
            <div className="time-stepper">
              <button type="button" onClick={() => stepUnit(unit.key, -1, unit.max)}>−</button>
              <input
                type="text"
                inputMode="numeric"
                maxLength="2"
                value={draft[unit.key]}
                onFocus={(event) => event.target.select()}
                onChange={(event) => {
                  const digits = event.target.value.replace(/\D/g, '').slice(0, 2);
                  const next = digits && Number(digits) > unit.max ? String(unit.max) : digits;
                  setDraft((current) => ({ ...current, [unit.key]: next }));
                }}
                onBlur={() => changeUnit(unit.key, draft[unit.key], unit.max)}
                aria-label={unit.label}
              />
              <button type="button" onClick={() => stepUnit(unit.key, 1, unit.max)}>+</button>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="order-picker-apply"
        onClick={() => {
          const normalized = units.map((unit) => String(
            Math.min(Math.max(Number(draft[unit.key]) || 0, 0), unit.max)
          ).padStart(2, '0'));
          onChange(normalized.join(':'));
          onClose();
        }}
      >
        Apply time
      </button>
    </PickerShell>
  );
};

export const TimezonePicker = ({ value, options, onChange, onClose }) => (
  <PickerShell title="Choose time zone" onClose={onClose} className="timezone-picker">
    <div className="timezone-options">
      {options.map((timezone) => (
        <button
          type="button"
          key={timezone.id}
          className={value === timezone.value ? 'is-selected' : ''}
          onClick={() => { onChange(timezone.value); onClose(); }}
        >
          {timezone.label}
        </button>
      ))}
    </div>
  </PickerShell>
);

const OrderTools = ({
  dateFrom,
  dateTo,
  dateFromTime = '00:00:00',
  dateToTime = '23:59:59',
  dateTimezone,
  onDateFromChange,
  onDateToChange,
  onDateFromTimeChange,
  onDateToTimeChange,
  onDateTimezoneChange,
  fetchOrders,
  pageType,
  filters = EMPTY_ORDER_FILTERS,
  onFiltersChange,
  onApplyFilters,
  statusFilter = 'all',
  onStatusChange,
  searchTerm = '',
  onSearch,
  searchPlaceholder = 'Search orders...'
}) => {
  const { user } = useAuth();
  const [activeModal, setActiveModal] = useState(null);
  const [activePicker, setActivePicker] = useState(null);
  const [filterFrom, setFilterFrom] = useState(dateFrom);
  const [filterTo, setFilterTo] = useState(dateTo);
  const [filterFromTime, setFilterFromTime] = useState(dateFromTime);
  const [filterToTime, setFilterToTime] = useState(dateToTime);
  const [filterTimezone, setFilterTimezone] = useState(dateTimezone || getBrowserTimezone());
  const [draftFilters, setDraftFilters] = useState(filters);
  const [openMultiSelect, setOpenMultiSelect] = useState(null);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    method: [], fiatCurrency: [], traderName: [], gateway: []
  });
  const [reportStatus, setReportStatus] = useState('all');
  const [reportFrom, setReportFrom] = useState(() => getDefaultDateRange()[0]);
  const [reportTo, setReportTo] = useState(() => getDefaultDateRange()[1]);
  const [reportFromTime, setReportFromTime] = useState('00:00:00');
  const [reportToTime, setReportToTime] = useState('23:59:59');
  const [reportTimezone, setReportTimezone] = useState(dateTimezone || getBrowserTimezone());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const invalidTableRange = Boolean(
    filterFrom && filterTo && `${filterFrom}T${filterFromTime}` > `${filterTo}T${filterToTime}`
  );
  const invalidReportRange = Boolean(
    reportFrom && reportTo && `${reportFrom}T${reportFromTime}` > `${reportTo}T${reportToTime}`
  );
  const selectedFilterCount = (filterFrom ? 1 : 0) + (filterTo ? 1 : 0)
    + Object.values(draftFilters || {}).reduce((total, value) => {
      if (Array.isArray(value)) return total + value.length;
      return total + (value !== '' && value !== null && value !== undefined ? 1 : 0);
    }, 0);
  const role = ['admin', 'support'].includes(user?.role) || user?.isAdmin ? 'admin' : user?.role || 'trader';
  const hasTableFilters = Boolean(dateFrom || dateTo || hasOrderFilterValues(filters));
  const activeFrom = activeModal === 'filters' ? filterFrom : reportFrom;
  const activeTo = activeModal === 'filters' ? filterTo : reportTo;
  const activeFromTime = activeModal === 'filters' ? filterFromTime : reportFromTime;
  const activeToTime = activeModal === 'filters' ? filterToTime : reportToTime;
  const activeTimezone = activeModal === 'filters' ? filterTimezone : reportTimezone;
  const timezoneOptions = getTimezoneOptions();
  const activeDatePreset = DATE_RANGE_PRESETS.find(({ key }) => {
    const [from, to] = getDateRangePreset(key);
    return from === filterFrom && to === filterTo;
  })?.key || '';

  useEffect(() => {
    if (!activeModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || exporting) return;
      if (activePicker) setActivePicker(null);
      else setActiveModal(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeModal, activePicker, exporting]);

  useEffect(() => {
    if (!activeModal) setActivePicker(null);
  }, [activeModal]);

  useEffect(() => {
    if (!activePicker) return undefined;
    const closeOnOutsideClick = (event) => {
      if (
        !event.target.closest('.order-picker-popover')
        && !event.target.closest('.order-inline-date')
        && !event.target.closest('.order-inline-time')
        && !event.target.closest('.order-inline-timezone')
      ) {
        setActivePicker(null);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [activePicker]);

  const openFilters = async () => {
    setFilterFrom(dateFrom);
    setFilterTo(dateTo);
    setFilterFromTime(dateFromTime);
    setFilterToTime(dateToTime);
    setFilterTimezone(dateTimezone || getBrowserTimezone());
    setDraftFilters({ ...(filters || {}) });
    setOpenMultiSelect(null);
    setActiveModal('filters');

    setFilterOptionsLoading(true);
    try {
      const options = await fetchOrderFilterOptions(pageType);
      setFilterOptions({
        method: options?.method || [],
        fiatCurrency: options?.fiatCurrency || [],
        traderName: options?.traderName || [],
        gateway: options?.gateway || []
      });
    } catch {
      setFilterOptions({ method: [], fiatCurrency: [], traderName: [], gateway: [] });
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  const openReport = () => {
    const [defaultFrom, defaultTo] = getDefaultDateRange();
    setReportFrom((current) => current || defaultFrom);
    setReportTo((current) => current || defaultTo);
    setExportError('');
    setActiveModal('report');
  };

  const applyFilters = () => {
    if (invalidTableRange) return;
    if (onApplyFilters) {
      onApplyFilters({
        dateFrom: filterFrom,
        dateTo: filterTo,
        dateFromTime: filterFromTime,
        dateToTime: filterToTime,
        dateTimezone: filterTimezone,
        filters: draftFilters
      });
    } else {
      onDateFromChange(filterFrom);
      onDateToChange(filterTo);
      onDateFromTimeChange(filterFromTime);
      onDateToTimeChange(filterToTime);
      onDateTimezoneChange(filterTimezone);
      onFiltersChange?.(draftFilters);
      if (draftFilters.statuses?.length && statusFilter !== 'all') onStatusChange?.('all');
    }
    setActiveModal(null);
  };

  const clearDraftFilters = () => {
    setFilterFrom('');
    setFilterTo('');
    setFilterFromTime('00:00:00');
    setFilterToTime('23:59:59');
    setDraftFilters({});
    setOpenMultiSelect(null);
  };

  const setActiveRange = (from, to) => {
    if (activeModal === 'filters') {
      setFilterFrom(from);
      setFilterTo(to);
    } else {
      setReportFrom(from);
      setReportTo(to);
    }
  };

  const applyDatePreset = (preset) => {
    const [from, to] = getDateRangePreset(preset);
    setFilterFrom(from);
    setFilterTo(to);
    setFilterFromTime('00:00:00');
    setFilterToTime('23:59:59');
  };

  const setActiveTimezone = (timezone) => {
    if (activeModal === 'filters') setFilterTimezone(timezone);
    else setReportTimezone(timezone);
  };

  const setActiveTime = (time, isEnd) => {
    if (activeModal === 'filters') {
      if (isEnd) setFilterToTime(time);
      else setFilterFromTime(time);
    } else if (isEnd) setReportToTime(time);
    else setReportFromTime(time);
  };

  const setDraftFilter = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const openPicker = (type, boundary, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActivePicker({
      type,
      boundary,
      anchor: { left: rect.left, bottom: rect.bottom }
    });
  };

  const pickerPosition = activePicker && typeof window !== 'undefined'
    ? (() => {
      const width = Math.min(380, window.innerWidth - 32);
      return {
        top: activePicker.anchor.bottom + 8,
        left: Math.max(16, Math.min(activePicker.anchor.left, window.innerWidth - width - 16)),
        width,
        maxHeight: Math.max(120, window.innerHeight - activePicker.anchor.bottom - 20)
      };
    })()
    : null;

  const handleExport = async () => {
    if (invalidReportRange) return;
    setExporting(true);
    setExportError('');

    try {
      const orders = await fetchReportOrders(
        fetchOrders,
        reportStatus,
        toRangeBoundary(reportFrom, false, reportTimezone, reportFromTime),
        toRangeBoundary(reportTo, true, reportTimezone, reportToTime)
      );
      let traderLookup = new Map();
      const reportRole = ['admin', 'support'].includes(user?.role) || user?.isAdmin ? 'admin' : user?.role || 'trader';

      if (reportRole === 'admin') {
        const tradersResult = await fetchTradersData(1, 10000);
        const traders = Array.isArray(tradersResult?.data) ? tradersResult.data : [];
        traderLookup = makeTraderLookup(traders);
      }

      const rows = buildReportRows(orders, reportRole, traderLookup, reportTimezone, pageType);
      const rangeLabel = reportFrom || reportTo
        ? `${reportFrom || 'beginning'}_${reportTo || 'today'}`
        : 'all-time';
      downloadCsv(rows, `${pageType}-${reportStatus}-${rangeLabel}.csv`);
      setActiveModal(null);
    } catch (error) {
      setExportError(error.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="order-tools">
      <div className="orders-action-bar">
        <div className="orders-action-search">
          <SearchBar
            onSearch={onSearch}
            value={searchTerm}
            showSearchState={false}
            placeholder={searchPlaceholder}
          />
        </div>
        <button
          type="button"
          className={`orders-action-button${hasTableFilters ? ' is-active' : ''}`}
          onClick={openFilters}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          <span>Filters</span>
          {hasTableFilters && <span className="orders-filter-indicator" aria-label="Filters active" />}
        </button>
        <button type="button" className="orders-action-button" onClick={openReport}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3v11m0 0 4-4m-4 4-4-4M5 17v2h14v-2" />
          </svg>
          <span>CSV</span>
        </button>
      </div>

      {activeModal && (
        <DetailsPortal>
          <>
          <div
            className={activeModal === 'filters' ? 'order-filters-overlay' : 'order-tools-modal-overlay'}
            onMouseDown={() => !exporting && setActiveModal(null)}
          >
            <section
              className={activeModal === 'filters' ? 'order-filters-panel' : 'order-tools-modal'}
              role="dialog"
              aria-modal="true"
              aria-labelledby="order-tools-modal-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <header className={activeModal === 'filters' ? 'order-filters-header' : 'order-tools-modal-header'}>
                <div className="order-tools-heading">
                  <h2 id="order-tools-modal-title">
                    {activeModal === 'filters' ? 'Filters' : 'Export CSV'}
                  </h2>
                  {activeModal === 'filters' && (
                    <span className="order-filters-count">
                      {selectedFilterCount} selected
                    </span>
                  )}
                  {activeModal === 'report' && (
                    <p>Choose a status and creation period for the report.</p>
                  )}
                </div>
                <div className="order-filters-header-actions">
                  {activeModal === 'filters' && (
                    <button
                      type="button"
                      className="order-filters-clear"
                      onClick={clearDraftFilters}
                      disabled={selectedFilterCount === 0}
                    >
                      Clear filters
                    </button>
                  )}
                  <button
                    type="button"
                    className={activeModal === 'filters' ? 'close-button' : 'order-tools-modal-close'}
                    onClick={() => setActiveModal(null)}
                    disabled={exporting}
                    aria-label="Close"
                  >
                    {activeModal === 'filters' ? 'Close' : (
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    )}
                  </button>
                </div>
              </header>

              <div className={activeModal === 'filters' ? 'order-filters-body' : 'order-tools-modal-body'}>
                {activeModal === 'report' && (
                  <label className="order-compact-select-row" htmlFor="report-status">
                    <span>Status</span>
                    <select
                      id="report-status"
                      value={reportStatus}
                      onChange={(event) => setReportStatus(event.target.value)}
                    >
                      {REPORT_STATUSES.map((status) => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </label>
                )}
                {activeModal === 'filters' && (
                  <>
                    <div className="order-filter-section">
                      <h3>General</h3>
                      <div className="order-filter-grid">
                        <MultiSelectField
                          label="Status"
                          value={draftFilters.statuses}
                          options={['Pending', 'Completed', 'Cancelled']}
                          open={openMultiSelect === 'statuses'}
                          onToggle={() => setOpenMultiSelect((current) => current === 'statuses' ? null : 'statuses')}
                          onChange={(value) => setDraftFilter('statuses', value)}
                        />
                      </div>
                    </div>
                  </>
                )}
                {activeModal === 'filters' && (
                  <div className="order-date-presets" role="group" aria-label="Quick date range">
                    {DATE_RANGE_PRESETS.map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        className={`order-date-preset${activeDatePreset === key ? ' is-selected' : ''}`}
                        onClick={() => applyDatePreset(key)}
                        aria-pressed={activeDatePreset === key}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="order-selected-dates">
                  <div className="order-selected-date">
                    <div>
                      <span>Date from</span>
                      <div className="order-selected-date-value">
                        <button
                          type="button"
                          className={`order-inline-date${activeFrom ? '' : ' is-placeholder'}`}
                          onClick={(event) => openPicker('date', 'from', event)}
                        >
                          <strong>{activeFrom ? formatSelectedDate(activeFrom) : 'Date from'}</strong>
                        </button>
                        {activeFrom && (
                          <>
                            <button
                              type="button"
                              className="order-inline-time"
                              onClick={(event) => openPicker('time', 'from', event)}
                            >
                              <strong>{activeFromTime}</strong>
                            </button>
                            <button
                              type="button"
                              className="order-inline-timezone"
                              onClick={(event) => openPicker('timezone', 'from', event)}
                            >
                              {getTimezoneOffsetLabel(activeTimezone)}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {activeFrom && (
                      <button
                        type="button"
                        className="order-selected-date-clear"
                        onClick={() => setActiveRange('', activeTo)}
                        aria-label="Clear start date"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="order-selected-date">
                    <div>
                      <span>Date to</span>
                      <div className="order-selected-date-value">
                        <button
                          type="button"
                          className={`order-inline-date${activeTo ? '' : ' is-placeholder'}`}
                          onClick={(event) => openPicker('date', 'to', event)}
                        >
                          <strong>{activeTo ? formatSelectedDate(activeTo) : 'Date to'}</strong>
                        </button>
                        {activeTo && (
                          <>
                            <button
                              type="button"
                              className="order-inline-time"
                              onClick={(event) => openPicker('time', 'to', event)}
                            >
                              <strong>{activeToTime}</strong>
                            </button>
                            <button
                              type="button"
                              className="order-inline-timezone"
                              onClick={(event) => openPicker('timezone', 'to', event)}
                            >
                              {getTimezoneOffsetLabel(activeTimezone)}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {activeTo && (
                      <button
                        type="button"
                        className="order-selected-date-clear"
                        onClick={() => setActiveRange(activeFrom, '')}
                        aria-label="Clear end date"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                {activeModal === 'filters' && FILTER_FIELD_GROUPS.map((group) => {
                  const visibleFields = group.fields.filter((field) => field.roles.includes(role));
                  if (!visibleFields.length) return null;
                  return (
                    <div className="order-filter-section" key={group.title}>
                      <h3>{group.title}</h3>
                      <div className="order-filter-grid">
                        {visibleFields.map((field) => field.type === 'multi' ? (
                          <MultiSelectField
                            key={field.key}
                            label={field.label}
                            value={draftFilters[field.key]}
                            options={filterOptions[field.key] || []}
                            loading={filterOptionsLoading}
                            open={openMultiSelect === field.key}
                            onToggle={() => setOpenMultiSelect((current) => current === field.key ? null : field.key)}
                            onChange={(value) => setDraftFilter(field.key, value)}
                          />
                        ) : (
                          <label className="order-filter-field" key={field.key}>
                            <span>{field.label}</span>
                            <input
                              type={field.type === 'number' ? 'number' : 'text'}
                              inputMode={field.type === 'number' ? 'decimal' : undefined}
                              value={draftFilters[field.key] || ''}
                              placeholder={field.label}
                              onChange={(event) => setDraftFilter(field.key, event.target.value)}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {(activeModal === 'filters' ? invalidTableRange : invalidReportRange) && (
                  <div className="order-tools-error">End date must not be before start date.</div>
                )}
                {activeModal === 'report' && exportError && (
                  <div className="order-tools-error">{exportError}</div>
                )}
              </div>

              <footer className={activeModal === 'filters' ? 'order-filters-footer' : 'order-tools-modal-footer'}>
                {activeModal === 'filters' ? (
                  <button
                    type="button"
                    className="order-modal-button primary"
                    onClick={applyFilters}
                    disabled={invalidTableRange}
                  >
                    Apply filters
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="order-modal-button secondary"
                      onClick={() => setActiveModal(null)}
                      disabled={exporting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="order-modal-button primary"
                      onClick={handleExport}
                      disabled={exporting || invalidReportRange}
                    >
                      {exporting ? 'Preparing CSV...' : 'Download CSV'}
                    </button>
                  </>
                )}
              </footer>
            </section>
          </div>
          {activePicker && (
            <div className="order-picker-anchor" style={pickerPosition}>
                {activePicker.type === 'date' && (
                  <CalendarPicker
                    value={activePicker.boundary === 'from' ? activeFrom : activeTo}
                    min={activePicker.boundary === 'to' ? activeFrom || undefined : undefined}
                    max={activePicker.boundary === 'from' ? activeTo || undefined : undefined}
                    onChange={(value) => activePicker.boundary === 'from'
                      ? setActiveRange(value, activeTo)
                      : setActiveRange(activeFrom, value)}
                    onClose={() => setActivePicker(null)}
                  />
                )}
                {activePicker.type === 'time' && (
                  <TimePicker
                    value={activePicker.boundary === 'from' ? activeFromTime : activeToTime}
                    onChange={(value) => setActiveTime(value, activePicker.boundary === 'to')}
                    onClose={() => setActivePicker(null)}
                  />
                )}
                {activePicker.type === 'timezone' && (
                  <TimezonePicker
                    value={activeTimezone}
                    options={timezoneOptions}
                    onChange={setActiveTimezone}
                    onClose={() => setActivePicker(null)}
                  />
                )}
            </div>
          )}
          </>
        </DetailsPortal>
      )}
    </div>
  );
};

export default OrderTools;
